import React, { useState, useRef, useEffect } from 'react';
import { Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import SidebarDashboard from './SidebarDashboard/SidebarDashboard';
import JobForm from './JobForm';
import JobsView from './JobsView';
import ProfileView from './ProfileView';
import { sideBarItemsEmployee, sideBarItemsCompany } from '../../../utils/sideBarItems';
import ApplicantsView from './Company/Applicants/ApplicantsView/ApplicantsView';
import Applications from './Applications';
import CreateJobForm from './CreateJobForm';
import JobDetail from '../../Jobs/JobDetail/JobDetail';
import ApplicationDetail from './Company/Applicants/ApplicationDetail/ApplicationDetail';
import Chat from './Chat';
import UsersView from './UsersView';
import PasswordReset from '../../Auth/PasswordReset';
import InboxView from './Inbox/InboxView';
import MyTalentsView from './Company/MyTalents/MyTalentsView';
import SearchTalentsView from './Company/SearchTalents/SearchTalentsView';
import FavouriteJobsView from './FavouriteJobsView';
import TalentApplicationView from './Company/TalentsRequests/TalentApplicationView/TalentApplicationView';
import UserForm from './UserForm';
import RestaurantManager from './Company/RestaurantManager/RestaurantManager';
import './dashboardView.css'
import { barsIcons } from '../../../utils/icons';
import { useAuth } from '../../../context/AuthContext';

// Protected route component for admin-only pages
const AdminOnlyRoute = ({ element, userRole, fallbackElement }) => {
  if (userRole === 'admin') {
    return element;
  }
  return fallbackElement || <div>Access denied. Admin privileges required.</div>;
};

// Wrapper component for edit user form
const EditUserForm = () => {
  const navigate = useNavigate();
  const [flashMessage, setFlashMessage] = useState(null);

  const handleFormSubmit = () => {
    setFlashMessage('Usuario actualizado exitosamente');
    setTimeout(() => {
      navigate('/panel-empresa/usuarios');
    }, 2000);
  };

  const handleGoBack = () => {
    navigate('/panel-empresa/usuarios');
  };

  return (
    <div>
      {flashMessage && (
        <div className="flash-message success">
          <p>{flashMessage}</p>
        </div>
      )}
      <UserForm 
        isEditing={true} 
        onFormSubmit={handleFormSubmit}
        onGoBack={handleGoBack}
        setFlashMessage={setFlashMessage}
      />
    </div>
  );
};

// Wrapper for edit job - provides onJobCreated callback
const EditJobForm = () => {
  const navigate = useNavigate();

  const handleJobCreated = (message) => {
    navigate('/panel-empresa/trabajos', { state: { flashMessage: message } });
  };

  return <CreateJobForm isEditing={true} onJobCreated={handleJobCreated} />;
};

const DashboardView = ({ userType }) => {
  const sidebarItems = userType === 'employee' ? sideBarItemsEmployee : sideBarItemsCompany;
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const sidebarRef = useRef(null);
  const { userInfo } = useAuth();
  
  // Get user role from JWT token or userInfo - make it reactive
  const [userRole, setUserRole] = useState(null);

  useEffect(() => {
    
    // Get role from userInfo
    if (userInfo && userInfo.role) {
      setUserRole(userInfo.role);
    } else {
      setUserRole(null);
    }
  }, [userInfo]); 


  const toggleSidebar = () => {
    setIsSidebarOpen(prev => !prev);
  };

  const handleSidebarClose = () => {
    setIsSidebarOpen(false)
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        setIsSidebarOpen(false);
      }
    };

    if (isSidebarOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isSidebarOpen]);

  return (
    <div className="dashboard-container">
      <div className="dashboard-view__container">
        <div 
          className="dashboard-view__filter-icon" 
          onClick={toggleSidebar}>
            {barsIcons}
        </div>
        
        {/* Backdrop overlay */}
        {isSidebarOpen && (
          <div 
            className="dashboard-view__backdrop"
            onClick={handleSidebarClose}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              background: 'rgba(0, 0, 0, 0.5)',
              zIndex: 999,
            }}
          />
        )}
        
        <div ref={sidebarRef} className={`dashboard-view__side-filter-container ${isSidebarOpen ? 'open' : ''}`}>  
          <SidebarDashboard 
            sidebarItems={sidebarItems} 
            isOpen={isSidebarOpen}
            onClose={handleSidebarClose}
            userRole={userRole}
          />
        </div>
        <div className="dashboard-view__component-container">
          <Routes>
            <Route path="perfil" element={<RestaurantManager />} />
            <Route path="restaurantes" element={<RestaurantManager />} />
            <Route path="postulaciones" element={<Applications />} />
            <Route path="postulaciones/:applicationId" element={<ApplicationDetail dashboard={'employee'}/>} />
            <Route path="trabajos/postulantes/:applicationId" element={<ApplicationDetail dashboard={'company'} />} />
            <Route path="postulaciones/trabajos/:id" element={<JobDetail />} />
            <Route path="trabajos/:id/postulantes" element={<ApplicantsView mode={'Applicants'}/>} />
            <Route path="usuarios" element={<UsersView/>} />
            <Route path="buscador-talentos" element={<SearchTalentsView />} />        
            <Route path="mis-talentos" element={<MyTalentsView/>} />    
            <Route path="trabajos/:id" element={<EditJobForm />} />
            <Route path="usuarios/:id" element={<EditUserForm />} />
            <Route path="inbox-empleado/:conversationId/:restaurantUserId" element={<Chat userType={"profesionales"}/>} />
            <Route path="inbox-empresa/:conversationId/:employeeId" element={<Chat userType={"empresas"}/>} />
            <Route path="perfil-empleado" element={<ProfileView userType={'employee'} />} />
            <Route path="trabajos" element={<JobsView layoutType={'dashboard-company'} />} />
            <Route path="confirm-email" element={<PasswordReset />}/>
            <Route path="inbox-empresa" element={<InboxView userType={"empresas"}/>}/>
            <Route path="inbox-empleado" element={<InboxView userType={"profesionales"}/>}/>
            <Route path="trabajos-favoritos" element={<FavouriteJobsView/>}/>
            <Route path="solicitud-talentos" element={<TalentApplicationView/>}/>
          </Routes>
        </div>
      </div>
    </div>
  );
};

export default DashboardView;

